import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `<Hovercontext />` — the canonical user-facing React component.
 *
 *   import { Hovercontext } from "@feedbacker/hovercontext";
 *   {process.env.NODE_ENV === "development" && <Hovercontext />}
 *
 * State machine: collapsed launcher → expanded toolbar → selecting →
 * composing → back to selecting. Esc collapses back to the launcher
 * without losing annotations.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureAfsContext } from "./dom/captureContext.js";
import { getBoundingBox } from "./dom/boundingBox.js";
import { getCssSelector } from "./dom/elementPath.js";
import { isMobile } from "./dom/isMobile.js";
import { captureElementOuterHTML, captureElementScreenshot, } from "./dom/screenshot.js";
import { smartLabel } from "./dom/smartLabel.js";
import { SelectionEngine } from "./engine/SelectionEngine.js";
import { getFreezeReport } from "./engine/freeze.js";
import { useEngineState } from "./hooks/useEngineState.js";
import { AnnotationCard, } from "./components/AnnotationCard.js";
import { DragAreaCanvas } from "./components/DragAreaCanvas.js";
import { Highlighter } from "./components/Highlighter.js";
import { HovercontextToolbar } from "./components/HovercontextToolbar.js";
import { Launcher } from "./components/Launcher.js";
import { LiveMarker } from "./components/LiveMarker.js";
import { markerModeFor, } from "./components/Marker.js";
import { MobileBanner } from "./components/MobileBanner.js";
import { SettingsPanel } from "./components/SettingsPanel.js";
import { ShortcutsPanel } from "./components/ShortcutsPanel.js";
import { TextRangeHighlighter } from "./components/TextRangeHighlighter.js";
import { createAfsAnnotation } from "./schema/factory.js";
import { annotationsToMarkdown } from "./schema/serialize.js";
import { AnnotationStore, useAnnotationStore } from "./store/index.js";
import { DEFAULT_SETTINGS, } from "./settings/settings.js";
import { dispatchAnnotation } from "./webhook/dispatcher.js";
const writeClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    }
    catch {
        /* clipboard unavailable */
    }
};
const modeForCommit = (commit) => {
    if (commit.mode === "text")
        return "text";
    if (commit.mode === "multi-select" || commit.mode === "drag-area") {
        return "group";
    }
    return "element";
};
const defaultAnchor = () => {
    if (typeof window === "undefined")
        return { x: 1000, y: 600 };
    return { x: window.innerWidth - 24 - 48, y: window.innerHeight - 24 - 48 };
};
export const Hovercontext = ({ active = true, initialMode = "element", initialSettings, initialAnchor, onAnnotation, extraIgnoreRoots, }) => {
    const rootRef = useRef(null);
    const [settings, setSettings] = useState({
        ...DEFAULT_SETTINGS,
        ...(initialSettings ?? {}),
    });
    const [showSettings, setShowSettings] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [mobileDismissed, setMobileDismissed] = useState(false);
    const [layoutMode, setLayoutMode] = useState(false);
    const onMobile = useMemo(() => isMobile(), []);
    const [pendingCommit, setPendingCommit] = useState(null);
    const [pendingComputedStyles, setPendingComputedStyles] = useState(undefined);
    const [secondaryTarget, setSecondaryTarget] = useState(undefined);
    const [pickingSecondary, setPickingSecondary] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const markerCounter = useRef(0);
    /* ----- expanded/launcher state ---------------------------------- */
    const [expanded, setExpanded] = useState(false);
    const [anchor, setAnchor] = useState(initialAnchor ?? defaultAnchor());
    /* ----- store + engine ------------------------------------------ */
    const store = useMemo(() => new AnnotationStore({ persist: true }), []);
    const annotations = useAnnotationStore(store);
    const engine = useMemo(() => {
        const root = rootRef.current;
        return new SelectionEngine({
            ignoreRoots: root ? [root, ...(extraIgnoreRoots ?? [])] : (extraIgnoreRoots ?? []),
            initialMode,
            freeze: settings.blockInteraction ? {} : false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const root = rootRef.current;
        engine.ignoreRoots = root
            ? [root, ...(extraIgnoreRoots ?? [])]
            : extraIgnoreRoots ?? [];
    });
    const engineState = useEngineState(engine);
    /* Start engine when expanded, stop on collapse */
    useEffect(() => {
        if (!active)
            return;
        if (expanded)
            engine.start(initialMode);
        else
            engine.stop();
    }, [engine, active, expanded, initialMode]);
    /* commit → pending card */
    useEffect(() => {
        const offCommit = engine.on("compose-open", (commit) => {
            setPendingCommit(commit);
            setEditingId(null);
            setSecondaryTarget(undefined);
            try {
                const ctx = captureAfsContext(commit.primary, {
                    detail: settings.outputDetail,
                    anchor: commit.anchor,
                });
                setPendingComputedStyles(ctx.computedStyles);
            }
            catch {
                setPendingComputedStyles(undefined);
            }
        });
        const offCancel = engine.on("compose-cancel", () => {
            setPendingCommit(null);
            setPendingComputedStyles(undefined);
            setSecondaryTarget(undefined);
            setPickingSecondary(false);
        });
        const offSecondary = engine.on("secondary-picked", ({ element }) => {
            const rect = getBoundingBox(element);
            setSecondaryTarget({
                label: smartLabel(element),
                selector: getCssSelector(element),
                tagName: element.tagName.toLowerCase(),
                rect,
            });
            setPickingSecondary(false);
        });
        const offSecondaryCancel = engine.on("secondary-cancelled", () => {
            setPickingSecondary(false);
        });
        return () => {
            offCommit();
            offCancel();
            offSecondary();
            offSecondaryCancel();
        };
    }, [engine, settings.outputDetail]);
    /* keyboard shortcuts (only while expanded, no card open, not in input) */
    useEffect(() => {
        if (!expanded)
            return;
        const onKey = (e) => {
            const target = e.target;
            const inField = target?.tagName === "INPUT" ||
                target?.tagName === "TEXTAREA" ||
                target?.isContentEditable;
            if (e.key === "Escape" && showShortcuts) {
                setShowShortcuts(false);
                return;
            }
            if (e.key === "Escape" &&
                !pendingCommit &&
                !showSettings &&
                engineState.phase === "selecting") {
                setExpanded(false);
                return;
            }
            if (inField || pendingCommit || showSettings)
                return;
            // ? — open shortcuts cheat sheet
            if (e.key === "?" || (e.shiftKey && e.key === "/")) {
                e.preventDefault();
                setShowShortcuts((v) => !v);
                return;
            }
            // P — pause/resume animations
            if (e.key === "p" || e.key === "P") {
                e.preventDefault();
                engine.togglePaused();
                return;
            }
            // H — toggle markers
            if (e.key === "h" || e.key === "H") {
                e.preventDefault();
                engine.toggleMarkersHidden();
                return;
            }
            // Shift+C — copy markdown
            if (e.shiftKey && (e.key === "C" || e.key === "c")) {
                e.preventDefault();
                if (annotations.length > 0) {
                    void navigator.clipboard
                        .writeText(annotationsToMarkdown(annotations, settings.outputDetail))
                        .catch(() => { });
                }
                return;
            }
            // Shift+X — clear annotations
            if (e.shiftKey && (e.key === "X" || e.key === "x")) {
                e.preventDefault();
                store.clear();
                markerCounter.current = 0;
                return;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [
        expanded,
        pendingCommit,
        showSettings,
        showShortcuts,
        engineState.phase,
        engine,
        annotations,
        settings.outputDetail,
        store,
    ]);
    /* ----- toolbar handlers ----------------------------------------- */
    const onTogglePaused = () => engine.setFrozen(!engineState.frozen);
    const onToggleMarkers = () => engine.toggleMarkersHidden();
    const onCopyMarkdown = useCallback(async () => {
        if (annotations.length === 0)
            return;
        const md = annotationsToMarkdown(annotations, settings.outputDetail);
        await writeClipboard(md);
    }, [annotations, settings.outputDetail]);
    const onClear = useCallback(() => {
        store.clear();
        markerCounter.current = 0;
    }, [store]);
    const dispatchAll = useCallback(() => {
        const enabled = settings.webhooks.filter((w) => w.enabled);
        if (enabled.length === 0 || annotations.length === 0)
            return;
        for (const a of annotations) {
            void dispatchAnnotation(a, enabled);
        }
        void fetch(`${settings.mcpBaseUrl}/annotations`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ annotations }),
        }).catch(() => {
            /* MCP bridge optional */
        });
    }, [annotations, settings.webhooks, settings.mcpBaseUrl]);
    const onToggleLayoutMode = () => setLayoutMode((v) => !v);
    const onPromoteAnnotation = useCallback(async (id) => {
        await store.promote(id, settings.mcpBaseUrl);
    }, [store, settings.mcpBaseUrl]);
    /* ----- toolbar drag --------------------------------------------- */
    const dragState = useRef(null);
    const onToolbarDragStart = (e) => {
        const target = e.target;
        if (target.closest("button, input, textarea, select"))
            return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        dragState.current = {
            pointerId: e.pointerId,
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
        const onMove = (ev) => {
            if (!dragState.current || ev.pointerId !== dragState.current.pointerId) {
                return;
            }
            const x = ev.clientX - dragState.current.offsetX + rect.width / 2;
            const y = ev.clientY - dragState.current.offsetY + rect.height / 2;
            setAnchor({
                x: Math.min(Math.max(24, x), window.innerWidth - 24),
                y: Math.min(Math.max(24, y), window.innerHeight - 24),
            });
        };
        const onUp = (ev) => {
            if (!dragState.current || ev.pointerId !== dragState.current.pointerId) {
                return;
            }
            dragState.current = null;
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };
    /* ----- annotation commit ---------------------------------------- */
    const handleSubmit = useCallback(async (data) => {
        if (!pendingCommit)
            return;
        if (editingId) {
            // Re-write existing annotation.
            store.update(editingId, {
                comment: data.comment,
                kind: data.kind,
                ...(data.intent ? { intent: data.intent } : {}),
                ...(data.severity ? { severity: data.severity } : {}),
            });
            setPendingCommit(null);
            setEditingId(null);
            setPendingComputedStyles(undefined);
            engine.finishCompose();
            return;
        }
        markerCounter.current += 1;
        const selectedText = pendingCommit.textRange?.toString() ?? undefined;
        const context = captureAfsContext(pendingCommit.primary, {
            detail: settings.outputDetail,
            anchor: pendingCommit.anchor,
            ...(selectedText ? { selectedText } : {}),
        });
        // Opt-in attachments. Screenshot prompts the user via the browser's
        // standard picker — capture before mutating store so we don't strand
        // a half-built annotation if the user cancels the share dialog.
        const attachments = {};
        if (settings.captureScreenshots) {
            const png = await captureElementScreenshot(pendingCommit.primary);
            if (png)
                attachments.screenshot = png;
        }
        if (settings.captureOuterHTML) {
            const html = captureElementOuterHTML(pendingCommit.primary);
            if (html)
                attachments.outerHTML = html;
        }
        const annotation = createAfsAnnotation({
            comment: data.comment,
            kind: data.kind,
            ...(data.intent ? { intent: data.intent } : {}),
            ...(data.severity ? { severity: data.severity } : {}),
            context,
            isMultiSelect: pendingCommit.mode === "multi-select" ||
                pendingCommit.mode === "drag-area",
            ...(data.placement ? { placement: data.placement } : {}),
            ...(data.rearrange ? { rearrange: data.rearrange } : {}),
            ...(attachments.screenshot || attachments.outerHTML
                ? { attachments }
                : {}),
        });
        store.add(annotation);
        onAnnotation?.(annotation);
        if (settings.autoSend) {
            const enabled = settings.webhooks.filter((w) => w.enabled);
            if (enabled.length > 0) {
                void dispatchAnnotation(annotation, enabled);
            }
        }
        setPendingCommit(null);
        setPendingComputedStyles(undefined);
        setSecondaryTarget(undefined);
        engine.finishCompose();
    }, [
        pendingCommit,
        editingId,
        settings.outputDetail,
        settings.webhooks,
        settings.autoSend,
        settings.captureScreenshots,
        settings.captureOuterHTML,
        store,
        onAnnotation,
        engine,
    ]);
    const handleCancel = () => {
        setPendingCommit(null);
        setEditingId(null);
        setPendingComputedStyles(undefined);
        setSecondaryTarget(undefined);
        setPickingSecondary(false);
        engine.cancel();
    };
    const handlePickSecondary = useCallback(() => {
        setPickingSecondary(true);
        engine.startSecondaryPick();
    }, [engine]);
    const handleClearSecondary = useCallback(() => {
        setSecondaryTarget(undefined);
    }, []);
    /* ----- marker interactions -------------------------------------- */
    const onMarkerRemove = useCallback((id) => {
        store.remove(id);
    }, [store]);
    const onMarkerEdit = useCallback((annotation) => {
        // Re-open AnnotationCard pre-filled. We synthesise a SelectionCommit
        // that points at the annotation's stored position.
        const fakeCommit = {
            mode: annotation.isMultiSelect === true
                ? "multi-select"
                : annotation.selectedText
                    ? "text"
                    : "element",
            primary: document.body,
            additional: [],
            dragRect: null,
            textRange: null,
            anchor: annotation.boundingBox
                ? {
                    x: annotation.boundingBox.x + annotation.boundingBox.width,
                    y: annotation.boundingBox.y + annotation.boundingBox.height,
                }
                : { x: 200, y: 200 },
        };
        setPendingCommit(fakeCommit);
        setEditingId(annotation.id);
        setPendingComputedStyles(annotation.computedStyles);
    }, []);
    /* ----- render --------------------------------------------------- */
    if (!active)
        return null;
    const enabledWebhookCount = settings.webhooks.filter((w) => w.enabled).length;
    const showHighlighter = expanded && engineState.phase === "selecting" && engineState.mode !== "text";
    const showText = expanded && engineState.phase === "selecting" && engineState.mode === "text";
    const pendingMode = pendingCommit
        ? modeForCommit(pendingCommit)
        : "element";
    /*
     * Anchor-corner-aware positioning.
     *
     * The launcher is 48×48; its centre sits at `anchor`. When the user
     * clicks it the toolbar pill (much wider) replaces it — we want the
     * pill to grow TOWARD the screen centre, not off the edge.
     *
     *   anchor in right half → pin the pill's RIGHT edge at the launcher's
     *                          right edge → pill extends leftward.
     *   anchor in left half  → pin the pill's LEFT edge at the launcher's
     *                          left edge → pill extends rightward.
     *
     * Same for vertical so the annotation card stays visible too.
     */
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 720;
    const isRight = anchor.x > viewportW / 2;
    const isBottom = anchor.y > viewportH / 2;
    const anchorStyle = {
        position: "fixed",
        zIndex: 2147483647,
        ...(isRight
            ? { right: `${Math.max(8, viewportW - anchor.x - 24)}px` }
            : { left: `${Math.max(8, anchor.x - 24)}px` }),
        ...(isBottom
            ? { bottom: `${Math.max(8, viewportH - anchor.y - 24)}px` }
            : { top: `${Math.max(8, anchor.y - 24)}px` }),
    };
    return (_jsxs("div", { ref: rootRef, "data-hovercontext-root": true, children: [showHighlighter && _jsx(Highlighter, { state: engineState }), showText && _jsx(TextRangeHighlighter, { range: engineState.textRange }), expanded && engineState.mode === "drag-area" && (_jsx(DragAreaCanvas, { rect: engineState.dragRect })), !engineState.markersHidden &&
                annotations.map((a, i) => {
                    if (!a.boundingBox)
                        return null;
                    return (_jsx(LiveMarker, { annotation: a, number: annotations.length - i, onRemove: () => onMarkerRemove(a.id), onEdit: () => onMarkerEdit(a) }, a.id));
                }), onMobile && !mobileDismissed ? (_jsx(MobileBanner, { onDismiss: () => setMobileDismissed(true) })) : (_jsx("div", { style: anchorStyle, children: expanded ? (_jsx(HovercontextToolbar, { paused: engineState.frozen, frozenReport: getFreezeReport(), markersHidden: engineState.markersHidden, annotationCount: annotations.length, webhookCount: enabledWebhookCount, autoSend: settings.autoSend, layoutModeActive: layoutMode, layoutModeEnabled: settings.layoutModeEnabled, onTogglePaused: onTogglePaused, onToggleMarkers: onToggleMarkers, onCopyMarkdown: () => void onCopyMarkdown(), onClear: onClear, onSendToWebhooks: dispatchAll, onToggleLayoutMode: onToggleLayoutMode, onOpenSettings: () => setShowSettings(true), onClose: () => setExpanded(false), onDragStart: onToolbarDragStart })) : (_jsx(Launcher, { onExpand: () => setExpanded(true), hasPending: annotations.length > 0 })) })), showShortcuts && (_jsx(ShortcutsPanel, { onClose: () => setShowShortcuts(false) })), pendingCommit && !pickingSecondary && (_jsx(AnnotationCard, { label: editingId
                    ? annotations.find((a) => a.id === editingId)?.elementPath ??
                        "annotation"
                    : smartLabel(pendingCommit.primary), anchor: pendingCommit.anchor, markerMode: pendingMode, ...(pendingComputedStyles !== undefined
                    ? { computedStyles: pendingComputedStyles }
                    : {}), ...(secondaryTarget ? { secondaryTarget } : {}), ...(editingId
                    ? (() => {
                        const a = annotations.find((x) => x.id === editingId);
                        if (!a)
                            return {};
                        return {
                            initialComment: a.comment,
                            ...(a.kind ? { initialKind: a.kind } : {}),
                            ...(a.intent ? { initialIntent: a.intent } : {}),
                            ...(a.severity ? { initialSeverity: a.severity } : {}),
                        };
                    })()
                    : {}), onPickSecondary: handlePickSecondary, onClearSecondary: handleClearSecondary, onSubmit: handleSubmit, onCancel: handleCancel })), pickingSecondary && (_jsxs(_Fragment, { children: [_jsx(Highlighter, { state: engineState, accent: "emerald" }), _jsxs("div", { className: "pointer-events-auto fixed left-1/2 top-6 -translate-x-1/2 z-[2147483647] flex items-center gap-3 px-4 py-2.5 rounded-full bg-hc-bg ring-1 ring-hc-good/40 shadow-toolbar animate-hc-pop", role: "status", children: [_jsx("span", { className: "h-2 w-2 rounded-full bg-hc-good animate-hc-pulse" }), _jsxs("span", { className: "text-[12px] text-hc-ink", children: ["Click an element on the page to set as ", _jsx("b", { children: "destination" })] }), _jsx("button", { type: "button", onClick: () => {
                                    setPickingSecondary(false);
                                    engine.cancelSecondaryPick();
                                }, className: "ml-2 text-[11px] uppercase tracking-wider text-hc-mute hover:text-hc-ink", children: "Cancel" })] })] })), showSettings && (_jsx(SettingsPanel, { settings: settings, annotations: annotations, onPromote: onPromoteAnnotation, onChange: setSettings, onClose: () => setShowSettings(false) }))] }));
};
//# sourceMappingURL=Hovercontext.js.map